import { AlertTriangleIcon } from '@orn/shared-web/icons';
import logger from '@orn/shared-web/lib/logger';
import * as React from 'react';

import { useLogout } from '../../hooks/mutations/useLogout';
import { useCurrentSession } from '../../hooks/queries/useCurrentSession';
import { PATHS } from '../../paths';

interface IBaseImpersonatorNoticeProps {
  /**
   * Callback when the logout button is clicked.
   */
  onLogout: () => void;
}

/**
 * Base visual component for displaying an impersonator notice.
 * Use this component directly when you need to control the logout behavior manually.
 * Wears the record-page alert pill treatment (`RecordAlertBannerBase`) so the
 * app-wide notices and the in-record banners read as one family.
 */
export const BaseImpersonatorNotice = ({ onLogout }: IBaseImpersonatorNoticeProps) => {
  return (
    <div className="bg-utility-error-50 border-utility-error-100 shadow-xs flex min-h-12 w-full items-center gap-2 rounded-2xl border px-4 py-2.5">
      <AlertTriangleIcon className="text-utility-error-500 size-[18px] shrink-0" />
      <span className="text-utility-error-700 min-w-0 flex-1 text-base font-medium">
        You are currently impersonating another user
      </span>
      <button
        onClick={onLogout}
        className="text-utility-error-500 hover:text-utility-error-700 shrink-0 text-base font-medium underline hover:no-underline"
        type="button"
      >
        Logout
      </button>
    </div>
  );
};

/**
 * Connected component that uses session hooks to determine impersonation state.
 * Only renders when the current session is impersonating another user.
 */
const ImpersonatorNotice = () => {
  const { session } = useCurrentSession();
  const [logout] = useLogout();
  const impersonatorUserId = session?.impersonatorUserId;

  const handleLogout = React.useCallback(async () => {
    try {
      await logout({ variables: {} });
      window.location.href = PATHS.LOGIN;
    } catch (error) {
      logger.error('Failed to logout:', error);
    }
  }, [logout]);

  if (!impersonatorUserId) {
    return null;
  }

  return <BaseImpersonatorNotice onLogout={handleLogout} />;
};

export default ImpersonatorNotice;
